import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { ToastProvider, LoadingScreen, IconButton } from "./components/v2n";
import MainLayout from "./layouts/MainLayout";
import { Locate } from "lucide-react";
import { LocationContext } from "./context/LocationContext";
import Footer from "./components/Footer";
import VibePassHomepagePromo from "./components/VibePassHomepagePromo";
import RouteIndexPolicyBoundary from "./components/RouteIndexPolicyBoundary";
import StaticSeoMetadataBoundary from "./components/StaticSeoMetadataBoundary";
import { trackPageView } from "./lib/analytics";
import { trackPlatformPageView } from "./lib/platformAnalytics";
import Disclaimer from "./pages/Disclaimer";
import { AREA_GUIDES } from "./data/areaGuides";
 const Landing = lazy(() => import("./pages/Landing"));
const VibePassPage = lazy(() => import("./pages/VibePassPage"));
const VibePassLoginPage = lazy(() => import("./pages/VibePassLoginPage"));
const VibePassMemberPage = lazy(() => import("./pages/VibePassMemberPage"));
const VibePassCheckInPage = lazy(() => import("./pages/VibePassCheckInPage"));
const VibePassPartnerRedeemPage = lazy(() => import("./pages/VibePassPartnerRedeemPage"));
const GoRedirectPage = lazy(() => import("./pages/GoRedirectPage"));
const VibeConciergeQrResolvePage = lazy(() => import("./pages/VibeConciergeQrResolvePage"));
const SalesLoginPage = lazy(() => import("./pages/SalesLoginPage"));
const SalesPortalPage = lazy(() => import("./pages/SalesPortalPage"));
const UsefulServicesPage = lazy(() => import("./pages/UsefulServicesPage"));
const AreaGuidePage = lazy(() => import("./pages/AreaGuidePage"));
const Home = lazy(() => import("./pages/home"));
const Brand = lazy(() => import("./pages/Brand"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminPlatformTraffic = lazy(() => import("./pages/AdminPlatformTraffic"));
const AdminDailyVenueIntelligence = lazy(() => import("./pages/AdminDailyVenueIntelligence"));
const AdminVenueIntelligencePage = lazy(() => import("./pages/AdminVenueIntelligencePage"));
const Owner = lazy(() => import("./pages/Owner"));
const Profile = lazy(() => import("./pages/Profile"));
const Explore = lazy(() => import("./pages/explore"));
const MapView = lazy(() => import("./pages/MapView"));
const VenuePage = lazy(() => import("./pages/VenuePage"));
const PublicVenuePage = lazy(() => import("./pages/PublicVenuePage"));
const VenueReportPage = lazy(() => import("./pages/VenueReportPage"));
const HotelQrGuidePage = lazy(() => import("./pages/HotelQrGuidePage"));
const VibeConciergePage = lazy(() => import("./pages/VibeConciergePage"));
const VenuesDirectoryPage = lazy(() => import("./pages/VenuesDirectoryPage"));
const DestinationCategoryPage = lazy(() => import("./pages/DestinationCategoryPage"));
const SeoVenueLandingPage = lazy(() => import("./pages/SeoVenueLandingPage"));
const DestinationsPage = lazy(() => import("./pages/DestinationsPage"));
const RestaurantsBenalmadena = lazy(() => import("./pages/RestaurantsBenalmadena"));
const BarsBenalmadena = lazy(() => import("./pages/BarsBenalmadena"));
const SportsBarsBenalmadena = lazy(() => import("./pages/SportsBarsBenalmadena"));
const WatchFootballBenalmadena = lazy(() => import("./pages/WatchFootballBenalmadena"));
const BeachBarsBenalmadena = lazy(() => import("./pages/BeachBarsBenalmadena"));
const ClubsBenalmadena = lazy(() => import("./pages/ClubsBenalmadena"));
const LiveMusicBenalmadena = lazy(() => import("./pages/LiveMusicBenalmadena"));
const TonightTorremolinos = lazy(() => import("./pages/TonightTorremolinos"));
const TonightBenalmadena = lazy(() => import("./pages/TonightBenalmadena"));
const TapasBenalmadena = lazy(() => import("./pages/TapasBenalmadena"));
const HotelGuidePreview = lazy(() => import("./pages/HotelGuidePreview"));
const Awards = lazy(() => import("./pages/Awards"));
const AwardDetail = lazy(() => import("./pages/AwardDetail"));
const Favourites = lazy(() => import("./pages/Favourites"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const ADMIN_STORAGE_KEY = "v2n_admin_session";

function AdminGuardedBrand() {
  const hasSession =
    typeof window !== "undefined" &&
    !!localStorage.getItem(ADMIN_STORAGE_KEY);

  if (!hasSession) return <Navigate to="/admin" replace />;
  return <Brand />;
}
function VenueRedirect() {
  const { id } = useParams();

  const redirects = {
    "1": "/venues/hole-in-the-wall-benalmadena",
    "2": "/venues/sky-bar-hotel-las-arenas-benalmadena",
    "3": "/venues/bonsai-shisha-lounge-benalmadena",
    "4": "/venues/kaleido-benalmadena",
    "5": "/venues/molly-malone-s-benalmadena",
    "9": "/venues/mullarkey-s-benalmadena-benalmadena",
    "10": "/venues/cherry-s-bar-benalmadena",
    "11": "/venues/casey-duke-s-irish-bar-benalmadena",
    "12": "/venues/sportsmans-bar-benalmadena",
    "13": "/venues/nix-sky-bar-benalmadena",
    "14": "/venues/maracas-beach-bil-bil-benalmadena",
    "15": "/venues/yucas-cafe-bar-benalmadena",
    "16": "/venues/o-hara-s-irish-pub-benalmadena",
    "17": "/venues/taro-lounge-puerto-marina-benalmadena",
    "18": "/venues/vinilo-cafe-pub-benalmadena",
    "19": "/venues/el-bar-de-las-motos-benalmadena",
    "20": "/venues/moonlight-bar-benalmadena",
  };

  return <Navigate to={redirects[id] || "/venues"} replace />;
}
function DestinationCategoryRoute({ city, cityLabel, category, title, intro }) {
  return (
    <DestinationCategoryPage
      city={city}
      cityLabel={cityLabel}
      category={category}
      title={title}
      intro={intro}
    />
  );
}
function LocalSeoRoute(props) {
  return <SeoVenueLandingPage {...props} />;
}
function isSensitiveVibePassSearch(pathname, search) {
  if (!pathname.startsWith("/vibe-pass/member")) return false;
  return new URLSearchParams(search || "").has("token");
}
function safeAnalyticsPathname(pathname) {
  if (pathname.startsWith("/vibe-pass/check-in/")) return "/vibe-pass/check-in";
  if (pathname.startsWith("/partner/vibe-pass/redeem/")) return "/partner/vibe-pass/redeem";
  if (pathname.startsWith("/q/")) return "/q";
  if (pathname.startsWith("/concierge/")) return "/concierge";
  return pathname;
}
export default function App() {
  const [myLocationFn, setMyLocationFn] = useState(() => () => {});
  const location = useLocation();
 useEffect(() => {
  const safePathname = safeAnalyticsPathname(location.pathname);
  const safeSearch = isSensitiveVibePassSearch(location.pathname, location.search) ? "" : location.search;
  trackPageView(safePathname, safeSearch);
  void trackPlatformPageView(safePathname);
}, [location.pathname, location.search]);
  return (
    <ToastProvider>
      <LocationContext.Provider value={{ setMyLocationFn }}>
        <Suspense fallback={<LoadingScreen />}>
          <MainLayout
            rightSlot={
              <IconButton
                onClick={() => myLocationFn()}
                aria-label="Use my location"
              >

                <Locate size={18} />
              </IconButton>
            }
          >
            <RouteIndexPolicyBoundary />
            <StaticSeoMetadataBoundary />
            <Routes>

           <Route path="/" element={<Landing />} />
<Route path="/vibe-pass" element={<VibePassPage />} />
<Route path="/vibe-pass/login" element={<VibePassLoginPage />} />
<Route path="/vibe-pass/member" element={<VibePassMemberPage />} />
<Route path="/vibe-pass/wallet" element={<VibePassMemberPage />} />
<Route path="/vibe-pass/check-in/:publicCode" element={<VibePassCheckInPage />} />
<Route path="/partner/vibe-pass/redeem/:claimToken" element={<VibePassPartnerRedeemPage />} />
<Route path="/go/:code" element={<GoRedirectPage />} />
<Route path="/q/:publicToken" element={<VibeConciergeQrResolvePage />} />
<Route path="/sales/login" element={<SalesLoginPage />} />
<Route path="/sales" element={<SalesPortalPage />} />
<Route path="/useful-services" element={<UsefulServicesPage />} />
<Route path="/benalmadena-bus-times" element={<UsefulServicesPage />} />
<Route path="/benalmadena-train-times" element={<UsefulServicesPage />} />
<Route path="/benalmadena-taxis" element={<UsefulServicesPage />} />
<Route path="/benalmadena-to-malaga-airport" element={<UsefulServicesPage />} />
<Route path="/pharmacies-benalmadena" element={<UsefulServicesPage />} />
<Route path="/medical-help-benalmadena" element={<UsefulServicesPage />} />
<Route path="/emergency-help-benalmadena" element={<UsefulServicesPage />} />
<Route path="/benalmadena-weather" element={<UsefulServicesPage />} />
<Route path="/markets-benalmadena" element={<UsefulServicesPage />} />
<Route path="/tourist-information-benalmadena" element={<UsefulServicesPage />} />
<Route path="/destinations" element={<DestinationsPage />} />
<Route path="/arroyo-de-la-miel" element={<AreaGuidePage guide={AREA_GUIDES.arroyo} />} />
<Route path="/benalmadena-pueblo" element={<AreaGuidePage guide={AREA_GUIDES.pueblo} />} />
<Route path="/admin/platform-traffic" element={<AdminPlatformTraffic />} />
<Route path="/admin/daily-intelligence" element={<AdminDailyVenueIntelligence />} />
<Route path="/admin/venue-intelligence/:venueId" element={<AdminVenueIntelligencePage />} />
<Route path="/venue/:id" element={<VenueRedirect />} />

<Route path="/favourites" element={<Favourites />} />



<Route path="/map" element={<MapView />} />


<Route
  path="/app"
  element={<Home registerLocationFn={setMyLocationFn} />}
/>
              <Route path="/explore" element={<Explore />} />
              <Route path="/disclaimer" element={<Disclaimer />} />
              <Route path="/brand" element={<AdminGuardedBrand />} />
              <Route path="/admin/*" element={<Admin />} />
              <Route path="/owner" element={<Owner />} />
              <Route path="/me" element={<Profile />} />
              <Route path="/venue-report/:venueSlug" element={<VenueReportPage />} />
              <Route path="/hotel/:hotelSlug" element={<HotelQrGuidePage />} />
              <Route path="/concierge/:publicToken" element={<VibeConciergePage />} />
             <Route path="/restaurants-benalmadena" element={<RestaurantsBenalmadena />} />
             <Route path="/tapas-benalmadena" element={<TapasBenalmadena />} />
             <Route path="/bars-benalmadena" element={<BarsBenalmadena />} />
             <Route path="/sports-bars-benalmadena" element={<SportsBarsBenalmadena />} />
             <Route path="/where-to-watch-football-benalmadena" element={<WatchFootballBenalmadena />} />
             <Route path="/beach-bars-benalmadena" element={<BeachBarsBenalmadena />} />
             <Route path="/clubs-benalmadena" element={<ClubsBenalmadena />} />
             <Route path="/live-music-benalmadena" element={<LiveMusicBenalmadena />} />
             <Route path="/tonight-benalmadena" element={<TonightBenalmadena />} />
             <Route path="/tonight-torremolinos" element={<TonightTorremolinos />} />
             <Route path="/tapas-benalmadena" element={<TapasBenalmadena />} />
             <Route
  path="/restaurants-puerto-marina"
  element={
    <LocalSeoRoute
      title="Puerto Marina Restaurants"
      city="Benalmadena"
      cityLabel="Puerto Marina"
      category="restaurants"
      search="puerto marina"
      intro="Explore restaurants around Puerto Marina for waterfront meals, dinner before drinks, family food stops and places close to bars, beach venues and nightlife."
      relatedLinks={[
        { label: "Benalmádena Restaurants", to: "/restaurants-benalmadena" },
        { label: "Puerto Marina Bars", to: "/bars-puerto-marina" },
        { label: "Beach Bars", to: "/beach-bars-benalmadena" },
        { label: "All Venues", to: "/venues" },
      ]}
      sections={[{ title: "Eating out around Puerto Marina", paragraphs: ["Puerto Marina is one of Benalmádena's strongest food and nightlife areas, with restaurants close to bars, coastal walks, boat views and evening drinks.", "Use Vibe2Nite to move from dinner to nearby venues without guessing where to go next."] }]}
      emptyTitle="More Puerto Marina restaurants coming soon"
      emptyText="Restaurants will appear here when the approved venue data includes Puerto Marina location details."
    />
  }
/>
<Route
  path="/bars-puerto-marina"
  element={
    <LocalSeoRoute
      title="Puerto Marina Bars"
      city="Benalmadena"
      cityLabel="Puerto Marina"
      category="bars"
      search="puerto marina"
      intro="Find bars around Puerto Marina for drinks, music, groups, cocktails and easy nights close to restaurants and the seafront."
      relatedLinks={[
        { label: "Benalmádena Bars", to: "/bars-benalmadena" },
        { label: "Puerto Marina Restaurants", to: "/restaurants-puerto-marina" },
        { label: "Live Music", to: "/live-music-benalmadena" },
        { label: "What's On Tonight", to: "/tonight-benalmadena" },
      ]}
      sections={[{ title: "Nightlife around Puerto Marina", paragraphs: ["Puerto Marina is useful for visitors who want food, bars and coastal nightlife in one area.", "Vibe2Nite links bar listings with venue pages, directions and nearby places so visitors can plan the next stop quickly."] }]}
      emptyTitle="More Puerto Marina bars coming soon"
      emptyText="Bars will appear here when the approved venue data includes Puerto Marina location details."
    />
  }
/>
<Route
  path="/restaurants-arroyo-de-la-miel"
  element={
    <LocalSeoRoute
      title="Arroyo de la Miel Restaurants"
      city="Benalmadena"
      cityLabel="Arroyo de la Miel"
      category="restaurants"
      search="arroyo"
      intro="Explore restaurants in Arroyo de la Miel for local dining, casual food, family meals and places close to bars, transport links and evening plans."
      relatedLinks={[
        { label: "Benalmádena Restaurants", to: "/restaurants-benalmadena" },
        { label: "Arroyo Bars", to: "/bars-arroyo-de-la-miel" },
        { label: "Tapas", to: "/tapas-benalmadena" },
        { label: "All Venues", to: "/venues" },
      ]}
      sections={[{ title: "Food and nightlife in Arroyo de la Miel", paragraphs: ["Arroyo de la Miel is useful for visitors looking for restaurants away from the seafront but still close to Benalmádena nightlife.", "The guide updates from approved venue data, so richer location tagging will make this page stronger over time."] }]}
      emptyTitle="More Arroyo de la Miel restaurants coming soon"
      emptyText="Restaurants will appear here when approved venue data includes Arroyo de la Miel location details."
    />
  }
/>
<Route
  path="/bars-arroyo-de-la-miel"
  element={
    <LocalSeoRoute
      title="Arroyo de la Miel Bars"
      city="Benalmadena"
      cityLabel="Arroyo de la Miel"
      category="bars"
      search="arroyo"
      intro="Find bars around Arroyo de la Miel for relaxed drinks, local nights and places near restaurants, transport links and Benalmádena evening plans."
      relatedLinks={[
        { label: "Benalmádena Bars", to: "/bars-benalmadena" },
        { label: "Arroyo Restaurants", to: "/restaurants-arroyo-de-la-miel" },
        { label: "Bonanza Square Bars", to: "/bars-bonanza-square" },
        { label: "What's On Tonight", to: "/tonight-benalmadena" },
      ]}
      sections={[{ title: "Local bars around Arroyo de la Miel", paragraphs: ["Arroyo de la Miel gives visitors another way to explore Benalmádena nightlife beyond the beachfront.", "Vibe2Nite keeps the page useful by showing approved venue listings and links back to related restaurant and bar guides."] }]}
      emptyTitle="More Arroyo de la Miel bars coming soon"
      emptyText="Bars will appear here when approved venue data includes Arroyo de la Miel location details."
    />
  }
/>
<Route
  path="/bars-bonanza-square"
  element={
    <LocalSeoRoute
      title="Bonanza Square Bars"
      city="Benalmadena"
      cityLabel="Bonanza Square"
      category="bars"
      search="bonanza"
      intro="Explore Bonanza Square bars for easy drinks, local nightlife and places close to Benalmádena restaurants, sports bars and live music."
      relatedLinks={[
        { label: "Benalmádena Bars", to: "/bars-benalmadena" },
        { label: "Sports Bars", to: "/sports-bars-benalmadena" },
        { label: "Restaurants", to: "/restaurants-benalmadena" },
        { label: "All Venues", to: "/venues" },
      ]}
      sections={[{ title: "Bars around Bonanza Square", paragraphs: ["Bonanza Square is a useful search area for visitors looking for local bars and an easy night out in Benalmádena.", "This page uses approved Vibe2Nite venue data and will improve as more venues are tagged with Bonanza Square details."] }]}
      emptyTitle="More Bonanza Square bars coming soon"
      emptyText="Bars will appear here when approved venue data includes Bonanza Square location details."
    />
  }
/>
<Route
  path="/seafood-restaurants-la-carihuela"
  element={
    <LocalSeoRoute
      title="Seafood Restaurants in La Carihuela"
      city="La Carihuela"
      cityLabel="La Carihuela"
      category="restaurants"
      search="seafood"
      intro="Explore seafood restaurants in La Carihuela for coastal dining, local food, relaxed evenings and places close to bars and beach-area nightlife."
      relatedLinks={[
        { label: "La Carihuela Restaurants", to: "/restaurants-la-carihuela" },
        { label: "La Carihuela Bars", to: "/bars-la-carihuela" },
        { label: "Beach Bars", to: "/beach-bars-la-carihuela" },
        { label: "Destinations", to: "/destinations" },
      ]}
      sections={[{ title: "Coastal food in La Carihuela", paragraphs: ["La Carihuela is known for coastal restaurants and relaxed nights close to the beach.", "Vibe2Nite keeps this guide tied to approved venue data, directions and related bars rather than thin static lists."] }]}
      emptyTitle="More La Carihuela seafood restaurants coming soon"
      emptyText="Seafood restaurants will appear here when approved venue data includes seafood tagging."
    />
  }
/>
             <Route
  path="/preview/hotel-guide/:hotelSlug"
  element={<HotelGuidePreview />}
/>

    <Route path="/restaurants-torremolinos"
  element={
    <DestinationCategoryRoute
      city="Torremolinos"
      cityLabel="Torremolinos"
      category="restaurants"
      title="Restaurants in Torremolinos"
      intro="Explore restaurants in Torremolinos for food before a night out, relaxed dining, local favourites and places close to bars, beaches and nightlife."
    />
  }
/>

<Route
  path="/bars-torremolinos"
  element={
    <DestinationCategoryRoute
      city="Torremolinos"
      cityLabel="Torremolinos"
      category="bars"
      title="Bars in Torremolinos"
      intro="Find bars in Torremolinos, from relaxed local pubs to nightlife spots, beach-area drinks and places to start the night."
    />
  }
/>

<Route
  path="/beach-bars-torremolinos"
  element={
    <DestinationCategoryRoute
      city="Torremolinos"
      cityLabel="Torremolinos"
      category="beach-bars"
      title="Beach Bars in Torremolinos"
      intro="Discover beach bars and seafront spots in Torremolinos for drinks, food, sea views and relaxed coastal nights."
    />
  }
/>

<Route
  path="/live-music-torremolinos"
  element={
    <DestinationCategoryRoute
      city="Torremolinos"
      cityLabel="Torremolinos"
      category="live-music"
      title="Live Music in Torremolinos"
      intro="Find live music venues, piano bars, jazz spots and entertainment venues in Torremolinos."
    />
  }
/>

<Route
  path="/restaurants-la-carihuela"
  element={
    <DestinationCategoryRoute
      city="La Carihuela"
      cityLabel="La Carihuela"
      category="restaurants"
      title="Restaurants in La Carihuela"
      intro="Explore restaurants in La Carihuela for seafood, relaxed dining, local favourites and food before a night out."
    />
  }
/>

<Route
  path="/bars-la-carihuela"
  element={
    <DestinationCategoryRoute
      city="La Carihuela"
      cityLabel="La Carihuela"
      category="bars"
      title="Bars in La Carihuela"
      intro="Find bars in La Carihuela for drinks, friendly local atmosphere, beach-area nights and relaxed evenings."
    />
  }
/>

<Route
  path="/beach-bars-la-carihuela"
  element={
    <DestinationCategoryRoute
      city="La Carihuela"
      cityLabel="La Carihuela"
      category="beach-bars"
      title="Beach Bars in La Carihuela"
      intro="Discover beach bars and coastal venues in La Carihuela for drinks, sea views, food and relaxed nights by the beach."
    />
  }
/>

<Route
  path="/live-music-la-carihuela"
  element={
    <DestinationCategoryRoute
      city="La Carihuela"
      cityLabel="La Carihuela"
      category="live-music"
      title="Live Music in La Carihuela"
      intro="Find live music and entertainment venues in La Carihuela as Vibe2Nite expands across the Costa del Sol."
    />
  }
/>
             <Route path="/awards/:slug" element={<AwardDetail />} />
             <Route path="/awards" element={<Awards />} />
             <Route path="/venues" element={<VenuesDirectoryPage />} />
              <Route path="/venues/:slug" element={<PublicVenuePage />} />
             <Route path="/privacy" element={<Privacy />} />
             <Route path="/terms" element={<Terms />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Footer/>
          </MainLayout>
          <VibePassHomepagePromo />
        </Suspense>
      </LocationContext.Provider>
    </ToastProvider>
  );
}
